import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { openThumbnails, RenderCancelledError, type PdfThumbnails } from '@/tools/pdf-engine';

const PREVIEW_CSS_WIDTH = 360;

/** The PDF just built or chosen, one page at a time, rendered by the PDF tool's pdf.js loader. */
export function PdfPreview({ pdf, pages }: { readonly pdf: Blob; readonly pages: number }) {
  const { t } = useTranslation('expenses');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [thumbs, setThumbs] = useState<PdfThumbnails | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let opened: PdfThumbnails | null = null;
    let active = true;
    setPage(0);
    openThumbnails(pdf)
      .then((t) => {
        opened = t;
        if (active) setThumbs(t);
        else void t.destroy();
      })
      .catch(() => undefined);
    return () => {
      active = false;
      setThumbs(null);
      void opened?.destroy();
    };
  }, [pdf]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!thumbs || !canvas) return;
    thumbs.render(page, canvas, PREVIEW_CSS_WIDTH).catch((error: unknown) => {
      if (!(error instanceof RenderCancelledError)) canvas.dataset.failed = 'true';
    });
    return () => thumbs.cancel(page);
  }, [thumbs, page]);

  return (
    <figure className="space-y-2">
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={t('capture.document.previewLabel', { page: page + 1, pages })}
        className="mx-auto block h-auto max-w-full rounded-md border border-border bg-white"
      />
      {pages > 1 && (
        <figcaption className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('capture.document.previous')}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft />
          </Button>
          <span className="tabular-nums">
            {t('capture.document.pageOf', { page: page + 1, pages })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('capture.document.next')}
            disabled={page >= pages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight />
          </Button>
        </figcaption>
      )}
    </figure>
  );
}
